import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import TextWithExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import copy_and_save from "./images/copy_and_save.png";
import generate_api_token from "./images/generate_api_token.png";
import go_to_admin_settings from "./images/go_to_admin_settings.png";
import go_to_api_token from "./images/go_to_api_token.png";
import ZendeskConnectForm from "./ZendeskConnectForm";

const ZendeskIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("zendesk");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerZendeskIntegration } = useAPI();

  const onSubmit = (values) => {
    // Save the form values for potential error recovery
    setFormValues(values);
    setLoading(true);
    registerZendeskIntegration(
      values.credentials.api_key,
      values.credentials.email,
      values.credentials.url
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Zendesk integration successful");
        // Clear form values on success
        setFormValues(null);
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically sync with Zendesk to enhance your customer support workflow."
            : ""
        }
        image={logos.ZendeskLogo}
        name={"Zendesk"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Zendesk is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}
                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Access the Zendesk Admin Settings">
                  <Typography variant={"body2"}>
                    Login to your Zendesk account with Admin privileges.
                  </Typography>
                  <TextWithExternalLink
                    text="Navigate to the "
                    linkText="Zendesk Admin Center"
                    href="https://www.zendesk.com/login/"
                  />

                  <Typography variant={"body2"}>
                    Click on the settings icon to move to the admin settings.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: go_to_admin_settings,
                        altext: "Go to Zendesk Admin Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    From the admin settings, click on "Apps and integrations" in
                    the sidebar.
                  </Typography>

                  <Typography variant={"body2"}>
                    Scroll down to the "APIs" section and click on "API tokens".
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: go_to_api_token,
                        altext: "Go to Zendesk Admin Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Generate an API Token">
                  <Typography variant={"body2"}>
                    On the API tokens page, click on the "Add API token" button.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: go_to_api_token,
                        altext: "Go to Zendesk Admin Settings",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Give your token a descriptive name, such as "BreezeToken".
                  </Typography>

                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Click "Save" to generate your API token.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: generate_api_token,
                        altext: "Generate API Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Save Your API Token">
                  <Typography variant={"body2"}>
                    After creating the token, Zendesk will display it once. Make
                    sure to copy it immediately as you won't be able to see it
                    again.
                  </Typography>
                  <Typography variant={"body2"}>
                    Copy the token to the box below. and click "Save".
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: copy_and_save,
                        altext: "Copy and Save API Token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>

                <GuidelineStep title="Connect to Breeze">
                  <Typography variant={"body2"}>
                    Now that you have your API token, you need to provide:
                  </Typography>
                  <ul>
                    <li>
                      <Typography variant={"body2"}>
                        Your Zendesk URL (e.g., https://yourcompany.zendesk.com)
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        The email address associated with the API token
                      </Typography>
                    </li>
                    <li>
                      <Typography variant={"body2"}>
                        The API token you just created
                      </Typography>
                    </li>
                  </ul>
                  <Typography variant={"body2"} sx={{ mt: 1 }}>
                    Fill out the form below to complete the integration.
                  </Typography>
                  <ZendeskConnectForm
                    onSubmit={onSubmit}
                    guideMode={guideMode}
                    savedFormValues={formValues}
                  />
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default ZendeskIntegration;
